/**
 * The Plugins window, a plugin browser like Unreal Engine's: shelves on the left (every plugin,
 * the built-in ones, the installed ones, then one per category: packs of kinds, languages,
 * tools...), a search and the install panel on top, and the plugins as cards, installed first.
 *
 * - Built-in plugins come with the app: the packs (the coding pack's kinds) stay on; the
 *   language analyzers can be turned off.
 * - Community plugins are a `plugin.kdl` manifest and, unless they only add kinds, a WebAssembly
 *   file, written in any language with an Extism kit (see the handbook, `docs/plugins/`). They
 *   can read code, add kinds, windows, commands and requests, and require other plugins. They
 *   run sandboxed (no files, no network, limited time and memory) and may only send the requests
 *   their permissions list: a plugin asking for permissions stays off until the user allows
 *   them. A plugin whose required plugins are not on waits for them.
 *
 * Everything goes through the plugins service (`state/plugins.ts`), which follows the core.
 * Plugin files dropped anywhere on the window are installed.
 */
import { For, Show, createMemo, createSignal, type JSX } from "solid-js";
import { useApp } from "../../app/context";
import { pluginShown, shelvesOf } from "../../state/plugins";
import { InstallSection } from "./InstallSection";
import { PluginCard } from "./PluginCard";

/** Where the authoring guide lives. */
const GUIDE =
  "https://github.com/Primitation/Devloppeur-App/blob/main/docs/plugins/README.md";

/** The shelf shown, the search and whether the install panel is open: kept while the window moves or closes. */
const [shelf, setShelf] = createSignal("all");
const [query, setQuery] = createSignal("");
const [adding, setAdding] = createSignal(false);

/** The Plugins window. */
export function PluginsWindow(): JSX.Element {
  const { plugins } = useApp();
  const [over, setOver] = createSignal(false);
  const shelves = createMemo(() => shelvesOf(plugins.list()));
  const shown = createMemo(() =>
    plugins.list().filter((p) => pluginShown(p, shelf(), query())),
  );
  const installed = () => shown().filter((p) => !p.builtin);
  const builtin = () => shown().filter((p) => p.builtin);

  return (
    <div
      class="pluginswin pbrowse"
      classList={{ "drop-over": over() }}
      data-plugins-drop
      onDragOver={(e) => {
        if (!e.dataTransfer?.types.includes("Files")) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={(e) =>
        e.currentTarget.contains(e.relatedTarget as Node) || setOver(false)
      }
      onDrop={(e) => {
        setOver(false);
        const files = e.dataTransfer?.files;
        if (!files?.length) return;
        e.preventDefault();
        void plugins.installFiles(files);
      }}
    >
      <div class="pbrowse-frame">
        <nav class="pbrowse-side" aria-label="Plugin shelves">
          <For each={shelves()}>
            {(s, i) => (
              <>
                <Show when={i() === 3}>
                  <h4>Categories</h4>
                </Show>
                <button
                  type="button"
                  class="pbrowse-shelf"
                  data-plugin-shelf={s.id}
                  aria-pressed={shelf() === s.id}
                  onClick={() => setShelf(s.id)}
                >
                  <span>{s.label}</span>
                  <em>{s.count}</em>
                </button>
              </>
            )}
          </For>
        </nav>
        <div class="pbrowse-main">
          <div class="pbrowse-bar">
            <button
              type="button"
              class="btn primary"
              id="plugins-add"
              aria-expanded={adding()}
              onClick={() => setAdding(!adding())}
            >
              + Install…
            </button>
            <input
              class="field"
              type="search"
              id="plugins-search"
              placeholder="Search plugins"
              value={query()}
              onInput={(e) => setQuery(e.currentTarget.value)}
            />
            <button
              type="button"
              class="btn"
              id="plugins-refresh"
              disabled={plugins.busy()}
              title="Read the plugin folder again"
              onClick={() => void plugins.refresh()}
            >
              Refresh
            </button>
          </div>
          <Show when={adding() || plugins.error()}>
            <InstallSection />
          </Show>
          <p class="small muted pbrowse-note" id="plugins-scope">
            {plugins.list().filter((p) => p.enabled).length
              ? "Enabled here means turned on in this project, for everyone working in it (one undo)."
              : "No plugin is on in this project yet: turn on the ones it needs, such as Coding."}
          </p>
          <div class="pbrowse-list">
            <Show when={installed().length}>
              <section class="grp">
                <h3>
                  Installed <span>{installed().length}</span>
                </h3>
                <ul class="plugin-list" id="plugins-installed">
                  <For each={installed()}>
                    {(plugin) => <PluginCard plugin={plugin} />}
                  </For>
                </ul>
              </section>
            </Show>
            <Show when={builtin().length}>
              <section class="grp">
                <h3>
                  Built in <span>{builtin().length}</span>
                </h3>
                <ul class="plugin-list" id="plugins-builtin">
                  <For each={builtin()}>
                    {(plugin) => <PluginCard plugin={plugin} />}
                  </For>
                </ul>
              </section>
            </Show>
            <Show when={!shown().length}>
              <p class="small muted pad" id="plugins-none">
                <Show when={query()} fallback="No plugin on this shelf yet.">
                  No plugin matches “{query()}”.
                </Show>
              </p>
            </Show>
            <section class="grp pbrowse-foot">
              <h3>Plugin folder</h3>
              <Show
                when={plugins.folder()}
                fallback={
                  <p class="small muted">Kept by this browser (IndexedDB).</p>
                }
              >
                <p class="small mono" id="plugins-folder-path">
                  {plugins.folder()}
                </p>
                <p class="small muted">
                  A plugin folder copied there by hand shows up after Refresh.
                </p>
              </Show>
              <p class="small muted">
                Write your own in Rust, Python, JavaScript, Go and more:{" "}
                <a href={GUIDE} target="_blank" rel="noopener noreferrer">
                  the plugin handbook
                </a>{" "}
                (<code>docs/plugins/</code>), with a step-by-step tutorial.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
